import React, { useState } from 'react';
import { useInView } from '../hooks/useInView';
import { useLanguage } from '../context/LanguageContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Gallery images - real photos from the salon
const galleryImages = [
  {
    id: 1,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/djo43s2a_salon1.jpg',
    alt: 'Studio 149 Salon Enterijer'
  },
  {
    id: 2,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/89zx5x4k_ana%20pere%20kosu%202.jpg',
    alt: 'Pranje kose u salonu'
  },
  {
    id: 3,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/60bo16jt_ana%20sisa.jpg',
    alt: 'Šišanje u Studio 149'
  },
  {
    id: 4,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/rmj19p3y_djurdja1.jpg',
    alt: 'Frizerka Đurđa'
  },
  {
    id: 5,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/5sgpmer1_frizura.jpg',
    alt: 'Moderna muška frizura'
  },
  {
    id: 6,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/cybeld34_Ana%20pere%20kosu.jpg',
    alt: 'Ana pere kosu'
  },
  {
    id: 7,
    url: 'https://customer-assets.emergentagent.com/job_studio149-barber/artifacts/11jihk69_crnac.jpg',
    alt: 'Šišanje u salonu'
  }
];

const GallerySection = () => {
  const { t } = useLanguage();
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => 
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <>
      <section
        id="galerija"
        ref={ref}
        className="py-24 lg:py-32 bg-[#0a0a0a]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            {t.gallery.label && (
              <span 
                className="text-[#0067ff] text-sm uppercase tracking-widest font-medium mb-4 block"
                style={{
                  opacity: isInView ? 1 : 0,
                  transform: isInView ? 'translateY(0) scaleY(1)' : 'translateY(-100px) scaleY(2)',
                  transition: 'all 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                }}
              >
                {t.gallery.label}
              </span>
            )}
            <h2 
              className="text-4xl md:text-5xl font-serif font-bold text-white mb-6"
              style={{
                opacity: isInView ? 1 : 0,
                letterSpacing: isInView ? 'normal' : '0.8em',
                transform: isInView ? 'translateY(0)' : 'translateY(50px)',
                transition: 'all 1.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.3s'
              }}
            >
              {t.gallery.title}
            </h2>
            {t.gallery.subtitle && (
              <h3 
                className="text-gray-400 text-lg font-light mb-6 max-w-2xl mx-auto"
                style={{
                  opacity: isInView ? 1 : 0,
                  transition: 'all 1s ease-out 0.5s'
                }}
              >
                {t.gallery.subtitle}
              </h3>
            )}
            <div 
              className="h-1 bg-[#0067ff] mx-auto"
              style={{
                width: isInView ? '5rem' : '0',
                opacity: isInView ? 1 : 0,
                transition: 'all 1.2s ease-out 0.6s'
              }}
            />
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={image.id}
                className="group relative aspect-square overflow-hidden bg-gray-900 rounded-lg cursor-pointer"
                style={{
                  opacity: isInView ? 1 : 0,
                  transform: isInView ? 'scale(1) rotate(0deg)' : 'scale(0.7) rotate(5deg)',
                  transition: `all 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${0.5 + index * 0.1}s`
                }}
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 border-2 border-[#0067ff] rounded-full flex items-center justify-center mx-auto mb-3 transition-transform duration-300 group-hover:rotate-90">
                      <span className="text-white text-2xl">+</span>
                    </div>
                    <p className="text-white text-sm uppercase tracking-widest">
                      {t.gallery.view}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 text-white hover:text-[#0067ff] transition-colors z-[60] p-4 bg-black/70 rounded-full"
            aria-label="Zatvori"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-0 top-0 bottom-0 w-24 md:w-32 flex items-center justify-start pl-4 text-white hover:text-[#0067ff] transition-colors z-50 bg-gradient-to-r from-black/30 to-transparent"
            aria-label="Prethodna"
          >
            <div className="p-3 bg-black/50 rounded-full">
              <ChevronLeft className="w-10 h-10" />
            </div>
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-0 top-20 bottom-0 w-24 md:w-32 flex items-center justify-end pr-4 text-white hover:text-[#0067ff] transition-colors z-50 bg-gradient-to-l from-black/30 to-transparent"
            aria-label="Sledeća"
          >
            <div className="p-3 bg-black/50 rounded-full">
              <ChevronRight className="w-10 h-10" />
            </div>
          </button>

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[85vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[currentImageIndex].url}
              alt={galleryImages[currentImageIndex].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>

          {/* Thumbnail Navigation */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex space-x-2">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-[#0067ff] w-6' 
                    : 'bg-white/50 hover:bg-white'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default GallerySection;